import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useUser } from "./userContext";
import api from "../api";

const StudentContext = createContext();

export const useStudentContext = () => useContext(StudentContext);

export const StudentProvider = ({ children }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useUser();

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem("token");
    console.log("Retrieved token:", token);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchStudentParticipation = useCallback(
    async (studentId, projectId) => {
      try {
        const response = await api.get(
          `/participations/${projectId}/participation/${studentId}`,
          {
            headers: getAuthHeader(),
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching student participation:", error);
        return null;
      }
    },
    [getAuthHeader]
  );

  const fetchStudents = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.log("No token found, skipping student fetch");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await api.get(
        "/projects/students",
        {
          headers: getAuthHeader(),
        }
      );
      const studentsWithParticipations = response.data; // The backend now returns complete participation details
      setStudents(studentsWithParticipations);
      setError(null);
    } catch (error) {
      console.error("Error fetching students:", error);
      setError("Failed to fetch students. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, fetchStudentParticipation]);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchStudents();
    } else {
      setStudents([]);
      setError(null);
      setLoading(false);
    }
  }, [user, fetchStudents]);

  const refreshStudentData = async (studentId) => {
    try {
      const updatedStudent = await fetchStudentWithParticipations(studentId);
      setStudents((prevStudents) =>
        prevStudents.map((student) =>
          student._id === studentId ? updatedStudent : student
        )
      );
    } catch (error) {
      console.error("Error refreshing student data:", error);
    }
  };

  const fetchStudentWithParticipations = async (studentId) => {
    const response = await api.get(
      `/projects/students/${studentId}`,
      {
        headers: getAuthHeader(),
      }
    );
    const student = response.data;
    // The backend now returns complete participation details in the student object
    return student;
  };

  const updateStudentRole = async (studentId, newRole) => {
    try {
      await api.put(
        `/users/${studentId}/role`,
        { role: newRole },
        { headers: getAuthHeader() }
      );
      await fetchStudents();
    } catch (error) {
      console.error("Error updating student role:", error);
      throw error;
    }
  };

  const resetStudentPassword = async (studentId, newPassword) => {
    try {
      await api.put(
        `/users/${studentId}/reset-password`,
        { newPassword },
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error("Error resetting student password:", error);
      throw error;
    }
  };

  const createAdminUser = async (adminData) => {
    try {
      await api.post(
        "/users/create-admin",
        adminData,
        { headers: getAuthHeader() }
      );
      await fetchStudents();
    } catch (error) {
      console.error("Error creating admin user:", error);
      throw error;
    }
  };

  const deleteUserAccount = async (userId) => {
    try {
      await api.delete(
        `/users/${userId}`,
        { headers: getAuthHeader() }
      );
      await fetchStudents();
    } catch (error) {
      console.error("Error deleting user account:", error);
      throw error;
    }
  };

  return (
    <StudentContext.Provider
      value={{
        students,
        loading,
        error,
        fetchStudents,
        refreshStudentData,
        fetchStudentWithParticipations,
        updateStudentRole,
        resetStudentPassword,
        createAdminUser,
        deleteUserAccount,
      }}>
      {children}
    </StudentContext.Provider>
  );
};
